import { useCallback, useState } from 'react'
import { useQuery } from '@apollo/client'
import type { ApolloError } from '@apollo/client'
import { IChannel, IChannelData, IChannelsData } from '@dtos/Channel'
import { GET_CHANNEL, GET_CHANNELS } from './queries/getChannels'

const mergeUniqueById = (
  prev: IChannel[],
  next: readonly IChannel[],
): IChannel[] => {
  const seen = new Set(prev.map((c) => c.id))
  const merged = [...prev]
  next.forEach((c) => {
    if (!seen.has(c.id)) {
      seen.add(c.id)
      merged.push(c)
    }
  })
  return merged
}

interface IGetChannelsProps {
  perPage?: number
  subject?: string
  skip?: boolean
}

interface IGetChannelsResult {
  channels: IChannel[]
  totalCount: number
  totalPages: number
  currentPage: number
  hasMore: boolean
  loadMore: () => Promise<void>
  loadingMore: boolean
  loading: boolean
  error: ApolloError | undefined
  refetch: () => Promise<void>
}

export const useGetChannels = ({
  perPage = 10,
  subject,
  skip,
}: IGetChannelsProps = {}): IGetChannelsResult => {
  const [accumulated, setAccumulated] = useState<IChannel[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [loadingMore, setLoadingMore] = useState(false)

  const { loading, error, refetch, fetchMore } = useQuery<IChannelsData>(
    GET_CHANNELS,
    {
      variables: { page: 1, perPage, subject },
      skip,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onCompleted: (result) => {
        const ch = result?.channels
        if (!ch) return
        setAccumulated(ch.channels ?? [])
        setCurrentPage(ch.currentPage ?? 1)
        setTotalPages(ch.totalPages ?? 1)
        setTotalCount(ch.totalCount ?? 0)
      },
    },
  )

  const loadMore = useCallback(async () => {
    if (loadingMore || loading) return
    if (currentPage >= totalPages) return
    const nextPage = currentPage + 1
    setLoadingMore(true)
    try {
      const result = await fetchMore({
        variables: { page: nextPage, perPage, subject },
      })
      const ch = result.data?.channels
      if (!ch) return
      setAccumulated((prev) => mergeUniqueById(prev, ch.channels ?? []))
      setCurrentPage(ch.currentPage ?? nextPage)
      setTotalPages(ch.totalPages ?? totalPages)
      setTotalCount(ch.totalCount ?? totalCount)
    } finally {
      setLoadingMore(false)
    }
  }, [
    currentPage,
    fetchMore,
    loading,
    loadingMore,
    perPage,
    subject,
    totalCount,
    totalPages,
  ])

  const handleRefetch = useCallback(async () => {
    setAccumulated([])
    setCurrentPage(1)
    await refetch({ page: 1, perPage, subject })
  }, [perPage, subject, refetch])

  return {
    channels: accumulated,
    totalCount,
    totalPages,
    currentPage,
    hasMore: currentPage < totalPages,
    loadMore,
    loadingMore,
    loading,
    error,
    refetch: handleRefetch,
  }
}

interface IGetChannelProps {
  id: string
  skip?: boolean
}

interface IGetChannelResult {
  data: IChannelData | undefined
  loading: boolean
  error: ApolloError | undefined
  refetch: () => void
}

export const useGetChannel = ({
  id,
  skip,
}: IGetChannelProps): IGetChannelResult => {
  const { data, loading, error, refetch } = useQuery<IChannelData>(
    GET_CHANNEL,
    {
      variables: { id },
      skip: skip || !id,
      fetchPolicy: 'cache-and-network',
    },
  )

  return { data, loading, error, refetch: () => refetch() }
}
